import { z } from "zod";

/**
 * The global MCP integration catalog, and the shape of the few things a USER is
 * allowed to send about it.
 *
 * ── What changed, and what deliberately did not ─────────────────────────────
 *
 * The catalog used to be CONFIGURATION, delivered as JSON in
 * `COPILOT_MCP_SERVERS` and parsed once at boot. The comment that stood here
 * argued that this was a security property: with no table, there was no column
 * capable of holding an endpoint, an allowlist or an OAuth client secret, so
 * "a user cannot register an MCP server" was a fact about the shape.
 *
 * The property was real. It was also being paid for by making every new
 * business system a DEPLOYMENT — an env edit, a compose edit, a restart — and
 * that price bought nothing, because the thing that actually has to be true is
 * narrower: *only an authorized administrator may write the catalog, and
 * nothing a normal user can send may ever reach it*. Migration 0053 moves the
 * catalog into PostgreSQL behind `copilot.mcp_integrations_manage`, and the
 * schemas below are what keep the narrower property true:
 *
 *   • the DEFINITION schema (`mcpIntegrationDefinitionSchema`) validates rows
 *     read back OUT of the database, not just requests coming in. A row edited
 *     by hand in psql gets the same reading an env entry got: https only, no
 *     credentials in a URL, a bounded allowlist, a bounded timeout. An
 *     integration whose row does not validate is dropped from the runtime
 *     catalog with the reason logged;
 *   • the definition carries NO SECRET and has no field one could occupy. The
 *     OAuth client ID is here because a client id is public by construction;
 *     the client secret and any shared service-account credential live
 *     encrypted in their own table, behind their own repository, and reach
 *     exactly one caller — the token request that needs them;
 *   • nothing in this file will parse an endpoint, an auth strategy, an
 *     allowlist or a read-only policy out of an ordinary user's request body.
 *     The admin request schemas that WILL are in `copilot-mcp-admin.ts`, and
 *     every route that accepts one is mapped to the management action.
 *
 * What IS in the database besides the catalog is the per-user half from 0052:
 * one CONNECTION per (user, integration) holding that person's own encrypted
 * OAuth grant or personal token, and one PREFERENCE per (user, integration,
 * tool). The catalog describes what exists and what is permitted; the
 * connection store says who connected and whose identity a call runs as.
 * Neither can stand in for the other, and that separation is unchanged.
 *
 * ── What a normal user may submit ───────────────────────────────────────────
 * Exactly three things, and every one of them is bounded here: a personal
 * token for a `token-user` integration, a boolean per tool, and an OAuth
 * callback's own parameters. Unchanged by the move to the database.
 */

/**
 * A stable integration id — the `slug` column, and the value every per-user
 * table carries in its `integration_id`.
 *
 * Lower-case, hyphen-separated, and length-bounded because it is the value a
 * browser sends to select an integration: it goes into an error message, a log
 * line, a stored audit row and a database key, so it may not be free text.
 *
 * It is IMMUTABLE once an integration exists. The admin API offers no way to
 * change it, because a rename would orphan every connection and preference
 * keyed to it — "everybody reconnects" is not a side effect a rename may have.
 */
export const mcpIconSchema = z.union([
  z.string().trim().max(60).regex(/^[a-z0-9 _-]+$/i),
  z
    .string()
    .trim()
    .max(2_048)
    .url()
    .refine(
      (value) => {
        const url = new URL(value);
        return url.protocol === "https:" && !url.username && !url.password;
      },
      { message: "Use an HTTPS logo URL without credentials." },
    ),
]);

export const mcpIntegrationIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, {
    message: "Use lower-case letters, digits and hyphens for an MCP integration id.",
  });

/** Business integrations explicitly selected for one chat turn. */
export const mcpIntegrationIdsSchema = z.array(mcpIntegrationIdSchema).max(10);

/**
 * A tool name, matched EXACTLY against the allowlist, against live discovery,
 * and against a stored preference.
 *
 * The character class is what makes tool-name spoofing a parse error rather
 * than a comparison to get right: no slashes, no dots, no whitespace, so a name
 * cannot be dressed up to look like a path or smuggle a second identifier past
 * a log line. MCP's own naming convention is well inside this.
 */
export const mcpToolNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, {
    message: "Tool names may contain letters, digits, underscores and hyphens.",
  });

/** A valid HTTP header name, for an integration that does not speak bearer. */
const httpHeaderNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/, {
    message: "Not a valid HTTP header name.",
  });

/** Hosts that mean "this machine", in the forms a URL can express them. */
export function isLoopbackHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "0:0:0:0:0:0:0:1" ||
    host.endsWith(".localhost")
  );
}

/**
 * An https URL that could not be pointed anywhere surprising.
 *
 * HTTPS is required, with ONE exception: a plain-http `localhost` URL,
 * permitted only when the caller passes `allowInsecureLocalhost` — which the
 * env layer sets from an explicit development flag and never in production.
 * Without the exception nobody can run an MCP server on their laptop; with it
 * unconditionally, a production deployment could be talked into cleartext.
 *
 * Credentials in the URL are refused outright. `https://user:pass@host/` is a
 * secret in a config field wearing a URL's clothes, and it would be logged
 * every time the URL was. That reading matters MORE now than it did under the
 * environment catalog, not less: the field is typed into a web form by an
 * administrator, and a form is a friendlier place to paste a token by accident
 * than a JSON blob in a deployment file.
 *
 * Used for the MCP endpoint AND for an OAuth provider's authorization, token
 * and revocation URLs, because all four are addresses this server will dial and
 * all four deserve the same reading.
 */
export function mcpHttpsUrlSchema(
  options: { allowInsecureLocalhost?: boolean; label?: string } = {},
) {
  const label = options.label ?? "MCP endpoint";
  return z
    .string()
    .trim()
    .min(1)
    .max(2_000)
    .superRefine((value, ctx) => {
      let url: URL;
      try {
        url = new URL(value);
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Expected an absolute URL for the ${label}.`,
        });
        return;
      }
      if (url.username || url.password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Do not put credentials in a URL. Enter them in the credential fields instead.",
        });
      }
      if (url.protocol === "https:") return;
      const local = isLoopbackHost(url.hostname);
      if (url.protocol === "http:" && local && options.allowInsecureLocalhost) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          url.protocol === "http:" && local
            ? "A plain-http localhost URL needs COPILOT_MCP_ALLOW_INSECURE_LOCALHOST=true, which is for development only."
            : `The ${label} must use https://.`,
      });
    });
}

/** Kept under its Phase 1 name; the endpoint is one of several URLs now. */
export const mcpEndpointSchema = (
  options: { allowInsecureLocalhost?: boolean } = {},
) => mcpHttpsUrlSchema({ ...options, label: "MCP endpoint" });

/**
 * How a user's own credential is placed on the outgoing MCP request.
 *
 * Chosen by the INTEGRATION DEFINITION and never by the browser. That is the
 * whole reason this is a separate type: a request body that could name a header
 * would let a user aim their token at a header of their choosing, which is a
 * request-smuggling primitive dressed up as a convenience. An ADMINISTRATOR may
 * choose it — that is the point of the wizard's "Credential transport" field —
 * and the same bound applies to them: a header NAME, validated as one.
 */
export const mcpCredentialTransportSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("bearer") }),
  z.object({ kind: z.literal("header"), header: httpHeaderNameSchema }),
]);

export type McpCredentialTransport = z.infer<typeof mcpCredentialTransportSchema>;

/**
 * An OAuth client id.
 *
 * Stored in plain `auth_config` and returned by the admin GET, because a client
 * id is public by construction — it travels in the authorization URL, in the
 * browser's address bar, in the provider's logs. Treating it as a secret would
 * be theatre that made the wizard worse (no "is this the right app?" check on
 * the edit screen) without protecting anything.
 *
 * The character bound is a paste guard rather than a format: providers issue
 * ids in wildly different shapes, but none of them contains whitespace, and an
 * administrator who pastes a whole `client_id=…&client_secret=…` line should
 * get a validation error rather than a confusing 400 from the provider.
 */
const oauthClientIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(400)
  .regex(/^\S+$/, { message: "An OAuth client id contains no spaces." });

/**
 * How an integration authenticates. Four strategies, and the split between them
 * is the point of the per-user design.
 *
 * `none`, `oauth2-user` and `token-user` never involve a deployment identity at
 * call time: the request carries the ASKING USER's credential or no credential
 * at all. `service-account` is the one mode in which every user's call
 * authenticates as the deployment, and it is spelled out rather than inferred:
 * it must state `shared: true`, which exists purely so that reading the
 * configuration tells you the truth about whose identity is being used.
 *
 * ── Where the secrets went ──────────────────────────────────────────────────
 *
 * Under the environment catalog these shapes carried `clientIdEnv`,
 * `clientSecretEnv` and `credentialEnv` — the NAME of a variable, never a
 * value, which was how "no secret is representable here" was enforced.
 *
 * The same property survives the move, by a different mechanism. There is no
 * `clientSecret` and no `credential` field in this union, so a secret is still
 * not representable in a definition: it lives in
 * `copilot_mcp_integration_secrets`, encrypted, read by exactly one layer. The
 * definition says WHICH strategy and WHERE to send it; the secret repository
 * answers WITH WHAT, and only for the caller that is about to make a token
 * request. The admin API's write schemas accept a secret once, on the way in,
 * and its read schemas cannot return one.
 */
export function mcpIntegrationAuthSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z.discriminatedUnion("kind", [
    /** A genuinely public endpoint. No credential exists to get wrong. */
    z.object({ kind: z.literal("none") }).strict(),

    /**
     * One deployment credential, used by every user of this integration.
     *
     * Modelled explicitly because there are legitimate cases — an internal
     * service with no per-user identity of its own — and refusing to model it
     * would push operators back to shapes that hid the fact. `shared` is a
     * literal `true` and not a boolean: there is no such thing as
     * `service-account` with `shared: false`, and a field that can only hold
     * one value is a field somebody has to choose on purpose. The admin UI
     * labels it as shared and discourages it in the same words.
     */
    z
      .object({
        kind: z.literal("service-account"),
        shared: z.literal(true),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
      })
      .strict(),

    /**
     * Each user pastes their own personal API token.
     *
     * `credentialLabel` is what the connect form asks for — "API key",
     * "Personal access token" — because business systems do not agree on the
     * name and a form that asks for the wrong thing gets the wrong thing.
     */
    z
      .object({
        kind: z.literal("token-user"),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
        credentialLabel: z.string().trim().min(1).max(60).default("API token"),
        /** Optional help text shown under the field, e.g. where to mint one. */
        credentialHint: z.string().trim().max(300).nullish(),
      })
      .strict(),

    /**
     * Each user connects their own account by OAuth 2.0 Authorization Code.
     *
     * `clientId` is an APPLICATION credential: it identifies YazamcoOps to the
     * provider and is the same for everybody. It is NOT the identity the MCP
     * call runs as — that is the per-user access token the code exchange
     * returns, which lives encrypted in the connection store and nowhere else.
     * Keeping the two in different tables, of different shapes, is what stops
     * one being read for the other.
     */
    z
      .object({
        kind: z.literal("oauth2-user"),
        authorizationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth authorization URL",
        }),
        tokenUrl: mcpHttpsUrlSchema({ ...options, label: "OAuth token URL" }),
        /** Optional; only used when the provider documents one. */
        revocationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth revocation URL",
        }).nullish(),
        clientId: oauthClientIdSchema,
        /**
         * Where this deployment's OAuth app was registered, when it was
         * registered rather than typed.
         *
         * Recorded and never used at call time: the authorization code flow
         * needs the three URLs above and the client id, and nothing here. It
         * exists so that a client id which came from RFC 7591 can be traced
         * back to the endpoint that issued it, and so a re-registration —
         * after a provider expires a dynamically registered client, which they
         * are entitled to do — has somewhere to go without an administrator
         * re-running discovery from the start.
         */
        registrationEndpoint: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth registration endpoint",
        }).nullish(),
        scopes: z.array(z.string().trim().min(1).max(200)).max(40).default([]),
        /**
         * PKCE, on by default.
         *
         * Off is a per-provider escape hatch for a server that rejects the
         * extra parameters rather than ignoring them, which some older
         * implementations do. It is a named opt-out and not a default, because
         * the default should be the safe one even for a confidential client.
         */
        pkce: z.boolean().default(true),
        /** Some providers require it on both the authorize and token calls. */
        audience: z.string().trim().max(200).nullish(),
        /**
         * A dot path into the token response naming the connected account, for
         * display only — "Connected as or@example.com". Providers disagree
         * wildly about where this lives (`account.email`, `login`, nothing at
         * all), so it is configuration rather than a guess, and a path that
         * finds nothing simply yields no name.
         */
        accountNameField: z.string().trim().max(120).nullish(),
        /** Same, for a stable id we can store beside the display name. */
        accountIdField: z.string().trim().max(120).nullish(),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
      })
      .strict(),
  ]);
}

export type McpIntegrationAuth = z.infer<ReturnType<typeof mcpIntegrationAuthSchema>>;

/** Which auth kinds require a connection belonging to the asking user. */
export function requiresUserConnection(auth: McpIntegrationAuth): boolean {
  return auth.kind === "oauth2-user" || auth.kind === "token-user";
}

/** Which auth kinds can have an application-level secret stored for them. */
export function supportsIntegrationSecret(kind: McpIntegrationAuth["kind"]): boolean {
  return kind === "oauth2-user" || kind === "service-account";
}

/**
 * One integration definition, as the RUNTIME sees it.
 *
 * This is the shape produced by reading a `copilot_mcp_integrations` row and
 * validating it — not a request body. Validating on the way out is the point:
 * the database is now the catalog, and a catalog nobody re-reads critically is
 * a catalog that trusts whatever last wrote to it. An administrator writes
 * through the admin schemas; a row could also arrive through a restore, a
 * migration, or somebody's psql session, and all four get this reading.
 *
 * `allowedTools` may be EMPTY here, which is the one substantive relaxation
 * from the environment schema and it is a consequence of the onboarding flow
 * rather than of the storage: an integration is created disabled, its
 * administrator connects their own account, discovery runs, and only then is
 * there an allowlist to write. An empty allowlist means nothing may be called;
 * the admin service additionally refuses to ENABLE such an integration, so the
 * state is a step in a wizard rather than something a user can encounter.
 *
 * `readOnly` defaults TRUE and `false` disables the integration rather than
 * enabling writes: there is no approval flow, so the honest behaviour for an
 * integration whose tools may mutate something is to refuse to call them.
 */
export function mcpIntegrationDefinitionSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z
    .object({
      id: mcpIntegrationIdSchema,
      displayName: z.string().trim().min(1).max(80),
      description: z.string().trim().max(300).nullish(),
      /** Branding, for the Business tools list. An icon token or an HTTPS logo URL. */
      icon: mcpIconSchema.nullish(),
      protocol: z.enum(["mcp", "text-webhook"]).default("mcp"),
      endpoint: mcpEndpointSchema(options),
      enabled: z.boolean().default(false),
      auth: mcpIntegrationAuthSchema(options).default({ kind: "none" }),
      /**
       * Per-integration upstream timeout. Bounded well below the Copilot turn's
       * own: automatic tool calls are spent BEFORE the question is asked, so
       * they are added to the user's wait rather than shared with it.
       */
      timeoutMs: z.coerce.number().int().min(1_000).max(60_000).default(15_000),
      allowedTools: z.array(mcpToolNameSchema).max(50).default([]),
      readOnly: z.boolean().default(true),
    })
    .strict()
    .superRefine((entry, ctx) => {
      if (entry.protocol !== "text-webhook") return;
      if (entry.auth.kind !== "none" && entry.auth.kind !== "service-account") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auth", "kind"],
          message: "A text webhook supports no authentication or one shared header credential.",
        });
      }
      if (
        entry.auth.kind === "service-account" &&
        entry.auth.transport.kind !== "header"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auth", "transport"],
          message: "A text webhook credential must be sent in a custom header.",
        });
      }
      if (entry.allowedTools.length !== 1 || entry.allowedTools[0] !== "search") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedTools"],
          message: 'A text webhook exposes exactly one tool named "search".',
        });
      }
    })
    .transform((entry) => ({
      ...entry,
      description: entry.description ?? null,
      icon: entry.icon ?? null,
      // Duplicates in an allowlist are harmless but make the count in an error
      // message wrong, and a set is what every check against it wants.
      allowedTools: [...new Set(entry.allowedTools)],
    }));
}

export type McpIntegrationDefinition = z.infer<
  ReturnType<typeof mcpIntegrationDefinitionSchema>
>;

/**
 * The whole catalog. Ids must be unique — enforced by the `slug` UNIQUE
 * constraint in the database, and re-checked here because this schema is also
 * what the legacy import path runs its candidate rows through, before any
 * constraint has had a chance to speak.
 */
export function mcpIntegrationCatalogSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z
    .array(mcpIntegrationDefinitionSchema(options))
    .max(50)
    .superRefine((entries, ctx) => {
      const seen = new Set<string>();
      for (const entry of entries) {
        if (seen.has(entry.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate MCP integration id "${entry.id}".`,
          });
        }
        seen.add(entry.id);
      }
    });
}

// ── What a user may send ─────────────────────────────────────────────────────

/**
 * One explicit, manually chosen retrieval — the admin/debug path.
 *
 * Three fields and nothing resembling a free-form payload. `arguments` is
 * checked here only for SHAPE — flat, primitive, bounded — because the
 * authoritative check is against the tool's own discovered `inputSchema`, which
 * this package cannot know. Doing the shape check here anyway means a hostile
 * body is rejected before it reaches a network call, and it is what makes
 * "arguments that bypass the discovered schema" unrepresentable rather than
 * merely unlikely: a nested object never gets as far as the schema validator.
 */
export const mcpRetrieveSchema = z.object({
  serverId: mcpIntegrationIdSchema,
  toolName: mcpToolNameSchema,
  arguments: z
    .record(
      z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, {
          message: "Tool argument names must be plain identifiers.",
        }),
      z.union([z.string().max(2_000), z.number().finite(), z.boolean()]),
    )
    .default({})
    .refine((args) => Object.keys(args).length <= 20, {
      message: "A tool call may carry at most 20 arguments.",
    }),
});

export type McpRetrieveInput = z.infer<typeof mcpRetrieveSchema>;

/**
 * The MCP context ids one chat turn may redeem manually.
 *
 * Capped at three for the same reason the pinned-file list is capped at five:
 * past a handful, a turn stops being a question with evidence attached and
 * becomes a request to read a database, which times out rather than answering.
 */
export const mcpContextIdsSchema = z.array(z.string().uuid()).max(3);

/**
 * A personal API token, as the browser submits it ONCE.
 *
 * One field. No header name, no endpoint, no auth kind — the integration
 * definition decides all three, and a body that could carry them would be a
 * body through which a user could redirect their own token or somebody else's.
 * The lower bound is a typo guard rather than a strength requirement: what
 * counts as a strong token is the provider's business, and refusing a short one
 * they issued would be us being wrong on their behalf.
 */
export const mcpTokenConnectionSchema = z
  .object({
    token: z.string().min(8).max(4_096),
  })
  .strict()
  .transform((body) => ({ token: body.token.trim() }))
  .refine((body) => body.token.length >= 8, {
    message: "Enter the personal token issued by the business system.",
  });

export type McpTokenConnectionInput = z.infer<typeof mcpTokenConnectionSchema>;

/**
 * A tool preference change. One boolean, and that is the entire authority the
 * browser has over what Copilot may call.
 */
export const mcpToolPreferenceSchema = z
  .object({ enabled: z.boolean() })
  .strict();

export type McpToolPreferenceInput = z.infer<typeof mcpToolPreferenceSchema>;

/**
 * Where to send the browser back to after an OAuth round-trip.
 *
 * Path-only and single-segment-safe: a full URL here would make the connect
 * flow an open redirect, and an open redirect on a callback that has just
 * established a credential is worth more to an attacker than most.
 */
export const mcpOauthReturnToSchema = z
  .string()
  .trim()
  .max(200)
  .regex(/^\/[A-Za-z0-9\-_/]*$/, { message: "Return path must be a local path." })
  .optional();

/**
 * The OAuth callback's own query, read defensively.
 *
 * `state` is required in every case, including the error case: an error
 * response that cannot be tied back to a pending connection is not a failure
 * this server has any business acting on. Everything else is optional because
 * a provider may send any subset.
 */
export const mcpOauthCallbackSchema = z.object({
  state: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(4_096).optional(),
  error: z.string().trim().max(200).optional(),
  error_description: z.string().trim().max(500).optional(),
});

export type McpOauthCallbackInput = z.infer<typeof mcpOauthCallbackSchema>;
